// Gate registry G1–G9 and override codes [MB D4; 02 §8.3 gate registry, BR-API-002; 04 BR-DEV-004
// (G9); 03 core.override_event.gate_code + BUILD-PLAN §9]. One evaluation path for every gate.
import { z } from 'zod';
import { hasPermission } from '../auth/check.ts';
import type { PermissionKey } from '../auth/permissions.ts';
import type { Principal } from '../context/principal.ts';
import { defineEnum } from '../enums/define.ts';
import type { EnumValue } from '../enums/define.ts';
import type { ReadinessGateMode } from '../enums/types-ops.ts';
import { AppError } from '../kernel/errors.ts';
import type { ErrorParams } from '../kernel/errors.ts';

/** `core.override_event.gate_code` (03 set + 04 §16 #10 + 06 §16 #8). */
export const OVERRIDE_CODE = defineEnum('core.override_event.gate_code', [
  'G1_COSTED_STYLE',
  'G2_CUT_RELEASE',
  'G3_CUSTOMER_SKU',
  'G4_MARKET_LABEL',
  'LOT_MIX',
  'NEGATIVE_AVAILABLE',
  'HELD_STOCK',
  'CLOSED_PERIOD',
  'PRICE_TOLERANCE',
  'RETRO_EDIT',
  'PO_IMPORT_EXCEPTION',
  'STYLE_NUMBER_MANUAL',
  'G9_DEV_READINESS',
  'OVERSHIP_TOLERANCE',
  'OVER_RECEIPT',
  'OVERCUT',
  // CR-W2b-12 (CR-W2b-6 R-CORE-GL-1): GL account controls (07 BR-GL-004/005/007; were RETRO_EDIT).
  'CONTROL_ACCOUNT',
  'FX_RATE_MANUAL',
  'POSTING_CONTROL',
  // CR-BG2c (W2c.ap R-CORE-AP-3; were RETRO_EDIT + a reason prefix): BR-AP-005/006 possible duplicate, BR-AP-003
  // original document missing, BR-AP-007 terms differing from the supplier's.
  'AP_DUPLICATE',
  'AP_ORIGINAL_MISSING',
  'AP_TERMS',
  // CR-BG2c (W2c.ar-bank R-CORE-AR-3; were RETRO_EDIT): BR-AR-012 return credit before the goods are received,
  // BR-BNK-005 reopening a closed bank reconciliation.
  'AR_RETURN_UNRECEIVED',
  'BANK_REC_REOPEN',
  // CR-BG2d (W2d.cutting R-CORE-CUT-2; were audited only / RETRO_EDIT): BR-CUT shortfall `override` / `substitute`
  // decisions, cancel of a released slip and reversal of a cut report.
  'CUT_SHORTFALL',
  'CUT_CANCEL_RELEASED',
  // CR-BG2e (W2e.sage-bridge R-CORE-SB-3; was POSTING_CONTROL): BR-PAR-023 a Sage batch generated while an earlier one
  // of its type is not confirmed imported.
  'SAGE_BATCH_ORDER',
]);
export type OverrideCode = EnumValue<typeof OVERRIDE_CODE>;

export const GATE_ID = defineEnum('core.gate.id', [
  'G1',
  'G2',
  'G3',
  'G4',
  'G5',
  'G6',
  'G7',
  'G8',
  'G9',
]);
export type GateId = EnumValue<typeof GATE_ID>;

export interface GateDef {
  readonly id: GateId;
  readonly code: OverrideCode;
  /** Who may pass a failed check with a reason; null = not overridable. */
  readonly overridePermission: PermissionKey | null;
  /** For non-overridable gates: the action that legitimately resolves the block. */
  readonly resolvedBy: PermissionKey | null;
  readonly messageKey: string;
  /** G9 only: governed by setting `development.readiness_gate_mode`. */
  readonly modeSetting?: 'development.readiness_gate_mode';
}

const gate = (
  id: GateId,
  code: OverrideCode,
  overridePermission: PermissionKey | null,
  resolvedBy: PermissionKey | null,
): GateDef =>
  Object.freeze({ id, code, overridePermission, resolvedBy, messageKey: `errors.gate.${id}` });

export const GATES: Readonly<Record<GateId, GateDef>> = Object.freeze({
  /** Approved cost sheet before a style is orderable. */
  G1: gate('G1', 'G1_COSTED_STYLE', 'orders.override', null),
  /** Fabric AND trim confirmed before cut release. */
  G2: gate('G2', 'G2_CUT_RELEASE', 'cutting.override', null),
  /** Customer SKU on every colourway/size before dispatch. */
  G3: gate('G3', 'G3_CUSTOMER_SKU', 'shipping.override', null),
  /** Market label content complete before dispatch. */
  G4: gate('G4', 'G4_MARKET_LABEL', 'shipping.override', null),
  /** No lot mixing on a cut. */
  G5: gate('G5', 'LOT_MIX', 'cutting.override', null),
  /** Held stock never available — release the hold instead. */
  G6: gate('G6', 'HELD_STOCK', null, 'lots.release_hold'),
  /** Closed period — reopen instead. */
  G7: gate('G7', 'CLOSED_PERIOD', null, 'periods.reopen'),
  /** Imported PO never auto-released — a person approves. */
  G8: gate('G8', 'PO_IMPORT_EXCEPTION', null, 'po_import.approve'),
  /** Development readiness before cut release (off / warn / block). */
  G9: Object.freeze({
    ...gate('G9', 'G9_DEV_READINESS', 'cutting.override', null),
    modeSetting: 'development.readiness_gate_mode',
  }),
});

/** Override codes that are not G-gates, with who may pass them [04/05/06 rules; assumption where noted]. */
export const OVERRIDE_CODE_PERMISSIONS: Readonly<Record<OverrideCode, readonly PermissionKey[]>> =
  Object.freeze({
    G1_COSTED_STYLE: ['orders.override'],
    G2_CUT_RELEASE: ['cutting.override'],
    G3_CUSTOMER_SKU: ['shipping.override'],
    G4_MARKET_LABEL: ['shipping.override'],
    LOT_MIX: ['cutting.override'],
    NEGATIVE_AVAILABLE: ['inventory.override'],
    HELD_STOCK: [],
    CLOSED_PERIOD: [],
    PRICE_TOLERANCE: ['orders.override', 'purchasing.override', 'ap.override'],
    RETRO_EDIT: [
      'styles.override',
      'costing.override',
      'orders.override',
      'purchasing.override',
      'cutting.override',
      'fg_receiving.override',
      'inventory.override',
      'shipping.override',
      'gl.override',
      'ap.override',
      'ar.override',
    ],
    // CR-BG2d (W2d.po-import R-POI-CORE-4, 06 §3.8): a waived blocking import exception; G8 itself stays
    // non-overridable (a person approves the import).
    PO_IMPORT_EXCEPTION: ['orders.override'],
    STYLE_NUMBER_MANUAL: ['styles.override'],
    G9_DEV_READINESS: ['cutting.override'],
    OVERSHIP_TOLERANCE: ['shipping.override'],
    OVER_RECEIPT: ['purchasing.override', 'fg_receiving.override'],
    OVERCUT: ['cutting.override'],
    CONTROL_ACCOUNT: ['gl.override'],
    FX_RATE_MANUAL: ['gl.override'],
    POSTING_CONTROL: ['gl.override'],
    AP_DUPLICATE: ['ap.override'],
    AP_ORIGINAL_MISSING: ['ap.override'],
    AP_TERMS: ['ap.override'],
    // [assumption Q-AR-07]: `ar.override` for an unreceived return; reopening a reconciliation is the closer's
    // (`bank.close`) — the bank machine's reopen step still needs gl.override until the finance lane moves it.
    AR_RETURN_UNRECEIVED: ['ar.override'],
    BANK_REC_REOPEN: ['bank.close'],
    CUT_SHORTFALL: ['cutting.override'],
    CUT_CANCEL_RELEASED: ['cutting.override'],
    SAGE_BATCH_ORDER: ['gl.override'],
  });

/** Mandatory override reason length [02 §8.1]. */
export const OVERRIDE_REASON_MIN_LENGTH = 15;

/** Retry body on the same action endpoint: `{"override": {"gate": "G2", "reason": "..."}}` [BR-API-002]. */
export const overrideRequestSchema = z.object({
  gate: GATE_ID.schema,
  reason: z.string().trim().min(OVERRIDE_REASON_MIN_LENGTH).max(1000),
});
export type OverrideRequest = z.infer<typeof overrideRequestSchema>;

export interface GateCheck {
  readonly gate: GateId;
  readonly passed: boolean;
  readonly params?: ErrorParams;
  /** G9 only (setting value as of the document date). */
  readonly mode?: ReadinessGateMode;
}

export type GateDecision =
  | { readonly outcome: 'pass'; readonly gate: GateId }
  | { readonly outcome: 'warn'; readonly gate: GateId; readonly code: OverrideCode }
  | {
      readonly outcome: 'overridden';
      readonly gate: GateId;
      readonly code: OverrideCode;
      readonly permission: PermissionKey;
      readonly reason: string;
    };

/**
 * The single gate evaluation path. Failed + no override → 409 GATE_BLOCKED (`overridable` tells
 * the UI to offer "Override with reason"). The caller records `overridden`/`warn` decisions in
 * `core.override_event` + audit inside the same transaction [BR-AUD-002].
 */
export function evaluateGate(
  check: GateCheck,
  principal: Principal,
  override?: OverrideRequest,
): GateDecision {
  const def = GATES[check.gate];
  if (def.modeSetting) {
    const mode = check.mode ?? 'block';
    if (mode === 'off' || check.passed) return { outcome: 'pass', gate: def.id };
    if (mode === 'warn') return { outcome: 'warn', gate: def.id, code: def.code };
  } else if (check.passed) {
    return { outcome: 'pass', gate: def.id };
  }
  const perm = def.overridePermission;
  const canOverride = perm !== null && hasPermission(principal, perm);
  if (!override || override.gate !== def.id) {
    throw new AppError('GATE_BLOCKED', {
      messageKey: def.messageKey,
      gate: def.id,
      overridable: canOverride,
      params: {
        ...(check.params ?? {}),
        ...(def.resolvedBy ? { resolvedBy: def.resolvedBy } : {}),
      },
    });
  }
  if (perm === null) {
    throw new AppError('GATE_BLOCKED', {
      messageKey: def.messageKey,
      gate: def.id,
      overridable: false,
    });
  }
  if (!canOverride) throw new AppError('FORBIDDEN', { params: { permission: perm } });
  const reason = override.reason.trim();
  if (reason.length < OVERRIDE_REASON_MIN_LENGTH) {
    throw new AppError('REASON_REQUIRED', {
      fields: [
        {
          path: 'override.reason',
          code: 'too_small',
          messageKey: 'errors.gate.reasonTooShort',
          params: { min: OVERRIDE_REASON_MIN_LENGTH },
        },
      ],
    });
  }
  return { outcome: 'overridden', gate: def.id, code: def.code, permission: perm, reason };
}
